import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BillingPage() {
  const [loading, setLoading] = useState(false);
  const [me, setMe] = useState(null);
  const planSummary = [
    { id: "monthly", name: "Monthly", price: "$9.99", cadence: "per month" },
    { id: "yearly", name: "Yearly", price: "$100", cadence: "per year" },
  ];

  const fetchMe = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/me`, { withCredentials: true });
      setMe(resp.data);
    } catch {
      setMe(null);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const startTrial = async (plan) => {
    setLoading(true);
    try {
      const resp = await axios.post(
        `${API}/stripe/create-checkout-session`,
        { origin: window.location.origin, plan },
        { withCredentials: true },
      );
      if (resp.data?.url) {
        window.location.href = resp.data.url;
      } else {
        toast.error(resp.data?.detail || "Failed to start checkout");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to start checkout");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <Card className="w-full max-w-md" data-testid="billing-card">
        <CardHeader>
          <CardTitle className="text-lg" data-testid="billing-title">Billing</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600" data-testid="billing-description">
            Start a 14-day trial (card required). After checkout, you’ll be redirected back.
          </p>
          {me?.accessLevel === "free" ? (
            <p className="text-sm text-emerald-700" data-testid="billing-free-access-note">
              Your account is free access and does not require billing.
            </p>
          ) : null}
          <div
            data-testid="billing-plan-summary"
            className="rounded-lg border border-slate-200 bg-white px-3 py-3"
          >
            <div className="text-xs uppercase tracking-wide text-slate-500" data-testid="billing-plan-summary-label">
              Plan Summary
            </div>
            <div className="mt-2 space-y-2">
              {planSummary.map((plan) => (
                <div
                  key={plan.id}
                  data-testid={`billing-plan-${plan.id}`}
                  className="flex items-center justify-between rounded-md border border-slate-100 bg-slate-50 px-3 py-2"
                >
                  <div>
                    <div className="text-sm font-medium text-slate-900" data-testid={`billing-plan-${plan.id}-name`}>
                      {plan.name}
                    </div>
                    <div className="text-xs text-slate-500" data-testid={`billing-plan-${plan.id}-cadence`}>
                      {plan.cadence}
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-slate-900" data-testid={`billing-plan-${plan.id}-price`}>
                    {plan.price}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Button
              data-testid="billing-start-monthly-button"
              onClick={() => startTrial("monthly")}
              className="w-full bg-slate-900 hover:bg-slate-800"
              disabled={loading}
            >
              {loading ? "Redirecting..." : "Start Monthly trial"}
            </Button>
            <Button
              data-testid="billing-start-yearly-button"
              onClick={() => startTrial("yearly")}
              variant="outline"
              className="w-full"
              disabled={loading}
            >
              {loading ? "Redirecting..." : "Start Yearly trial"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
